/**
 * Team positions live (docs/PERMISSIONS.md §13): the team's stances on bills, its Alignment list and per-bill target
 * lists, as they change, from the dashboard or /api/v2 (worker/src/team-stance.js pushes them on the org channel,
 * `org:{orgId}`, OrgRoom). Applied where they're shown:
 *   - People · Advocacy overview: "Your team's positions" (peopleStore advocacy.positions);
 *   - Legislation list: each row's "You support / oppose" (listStore, myPosition);
 *   - Legislation detail: myPosition, the "Your team's position" client, the perspective switch and the target list
 *     (the open record's store).
 * They arrive on the org channel every team page opens (overview/live.ts, booted by main.tsx), which hands them to
 * onTeamDelta(); armTeamLive() in main.tsx says where the stores are. LiveChannel replays what was missed.
 */
import { toPosition, type Stance, type StancePosition } from "../lib/stance";
import type { TeamPosition } from "../people/model";
import type { LegislationSummary, ListData } from "../legislation/types";
import type { LegislationStore } from "../legislation/legislationStore";

export type TeamPositionsDelta =
  | { t: "stance.set"; recordId: string; matterId: string; stance: Stance | null; position: StancePosition | null; entry: TeamPosition | null; by?: string }
  | { t: "positions.saved"; positions: TeamPosition[]; by?: string }
  | { t: "targets.set"; recordId: string; matterId: string; onTargetList: string[]; contacted: string[] };

/** The stores a delta can touch (each optional; tests pass their own). */
export interface TeamLiveTargets {
  people?: { getState(): { advocacy: { positions: TeamPosition[] } | null }; setState(p: object): void };
  list?: { getState(): { data: ListData | null }; setState(p: object): void };
  detail?: LegislationStore | null;
}

const sameRecord = (a?: string | null, b?: string | null) => !!a && !!b && a.toUpperCase() === b.toUpperCase();

/** The list's rows with their position from `of(recordId)` (undefined = no stance); untouched rows keep their object. */
function mapRows(data: ListData, of: (recordId: string) => StancePosition | undefined | false): ListData {
  const row = (s: LegislationSummary) => {
    const p = of(s.recordId);
    if (p === false || p === s.myPosition) return s;
    const next = { ...s };
    if (p) next.myPosition = p; else delete next.myPosition;
    return next;
  };
  const rows = (xs: LegislationSummary[]) => xs.map(row);
  return {
    ...data,
    agenda: { ...data.agenda, byCommittee: data.agenda.byCommittee.map((g) => ({ ...g, items: rows(g.items) })) },
    recentFollowed: rows(data.recentFollowed),
    following: { ...data.following, upForVote: rows(data.following.upForVote), decided: rows(data.following.decided) },
    rollCalls: { ...data.rollCalls, items: rows(data.rollCalls.items) },
  };
}

/** One bill's stance on the open detail page: myPosition, the team's client row, and the perspective. */
function applyDetailStance(store: LegislationStore, recordId: string, position: StancePosition | null) {
  const s = store.getState(), d = s.detail;
  if (!d || !sameRecord(d.recordId, recordId) || (d.myPosition ?? null) === position) return;
  const clients = position ? d.lobbying.clients.map((c) => (c.isTeam ? { ...c, position } : c)) : d.lobbying.clients.filter((c) => !c.isTeam);
  const next = { ...d, lobbying: { ...d.lobbying, clients } };
  if (position) next.myPosition = position; else delete next.myPosition;
  store.setState({
    detail: next,
    ...(!position && s.perspective === "your" ? { perspective: "their" } : {}),
    ...(!position && s.clientId === "team" ? { clientId: clients[0]?.id ?? null } : {}),
  });
}

/** Applies one team-positions delta to the stores. Unknown deltas (other org-channel traffic) are ignored. */
export function applyTeamDelta(d: { t: string } & Record<string, unknown>, targets: TeamLiveTargets) {
  const x = d as TeamPositionsDelta;
  if (x.t === "stance.set") {
    const position = x.position ?? toPosition(x.stance);
    const adv = targets.people?.getState().advocacy;
    if (adv && targets.people) {
      const rest = adv.positions.filter((p) => !sameRecord(p.recordId, x.recordId));
      const at = adv.positions.findIndex((p) => sameRecord(p.recordId, x.recordId));
      const positions = !x.entry ? rest : at < 0 ? [...rest, x.entry] : adv.positions.map((p, i) => (i === at ? x.entry! : p));
      targets.people.setState({ advocacy: { ...adv, positions } });
    }
    const data = targets.list?.getState().data;
    if (data && targets.list) targets.list.setState({ data: mapRows(data, (r) => (sameRecord(r, x.recordId) ? position ?? undefined : false)) });
    if (targets.detail) applyDetailStance(targets.detail, x.recordId, position);
  } else if (x.t === "positions.saved") {
    const adv = targets.people?.getState().advocacy;
    if (adv && targets.people) targets.people.setState({ advocacy: { ...adv, positions: x.positions } });
    const byRecord = new Map(x.positions.filter((p) => p.recordId).map((p) => [p.recordId!.toUpperCase(), toPosition(p.stance) ?? undefined]));
    const data = targets.list?.getState().data;
    if (data && targets.list) targets.list.setState({ data: mapRows(data, (r) => byRecord.get(r.toUpperCase())) });
    const detail = targets.detail?.getState().detail;
    if (detail && targets.detail) applyDetailStance(targets.detail, detail.recordId, byRecord.get(detail.recordId.toUpperCase()) ?? null);
  } else if (x.t === "targets.set") {
    const s = targets.detail?.getState();
    if (s?.detail?.team && sameRecord(s.detail.recordId, x.recordId)) {
      targets.detail!.setState({ detail: { ...s.detail, team: { onTargetList: x.onTargetList, contacted: x.contacted } } });
    }
  }
}

// ---------- from the org channel

const TEAM_DELTAS = new Set(["stance.set", "positions.saved", "targets.set"]);
let resolve: () => TeamLiveTargets = () => ({});

/** main.tsx: where the stores are (so this module doesn't import the pages). */
export function armTeamLive(stores: () => TeamLiveTargets) {
  resolve = stores;
}

/** An org-channel delta (overview/live.ts): a team-positions delta is applied; anything else → false (not ours). */
export function onTeamDelta(d: { t: string }): boolean {
  if (!TEAM_DELTAS.has(d.t)) return false;
  applyTeamDelta(d as { t: string } & Record<string, unknown>, resolve());
  return true;
}
